// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Check, Copy } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createHighlighterCore } from "shiki/core";
import { createJavaScriptRegexEngine } from "shiki/engine/javascript";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const highlighterPromise = createHighlighterCore({
  themes: [import("@shikijs/themes/material-theme-lighter"), import("@shikijs/themes/material-theme")],
  langs: [
    import("@shikijs/langs/json"),
    import("@shikijs/langs/java"),
    import("@shikijs/langs/sql"),
    import("@shikijs/langs/regexp"),
  ],
  engine: createJavaScriptRegexEngine(),
});

export type CodeLanguage = "json" | "sql" | "regexp" | "cel";

/** Map custom language aliases to shiki grammar names */
const LANG_GRAMMAR: Partial<Record<CodeLanguage, string>> = {
  cel: "java",
};

function formatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

interface CodeHighlightProps {
  value: string;
  lang: CodeLanguage;
  /** Auto-format the value (e.g. pretty-print JSON). Defaults to true for JSON. */
  format?: boolean;
  /** Show the hover copy button. Turn off for short, dense snippets where it only adds noise. */
  copyable?: boolean;
  className?: string;
}

export function CodeHighlight({ value, lang, format, copyable = true, className }: CodeHighlightProps) {
  const { t } = useTranslation("back-office");
  const shouldFormat = format ?? lang === "json";
  const formatted = useMemo(
    () => (shouldFormat && lang === "json" ? formatJson(value) : value),
    [value, lang, shouldFormat],
  );
  const [html, setHtml] = useState<string>("");
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(formatted);
    setCopied(true);
    toast.success(t("common.copiedToClipboard"));
    setTimeout(() => setCopied(false), 2000);
  }

  useEffect(
    function highlightSyntax() {
      let cancelled = false;
      highlighterPromise.then((h) => {
        if (cancelled) return;
        setHtml(
          h.codeToHtml(formatted, {
            lang: LANG_GRAMMAR[lang] ?? lang,
            themes: { light: "material-theme-lighter", dark: "material-theme" },
          }),
        );
      });
      return () => {
        cancelled = true;
      };
    },
    [formatted, lang],
  );

  const copyButton = copyable ? (
    <Button
      variant="ghost"
      size="icon"
      className="absolute top-2 right-2 h-7 w-7 opacity-0 transition-opacity group-hover/code:opacity-100"
      onClick={handleCopy}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  ) : null;

  if (!html) {
    return (
      <div className="group/code relative">
        {copyButton}
        <pre
          className={cn(
            "overflow-auto rounded-md text-xs font-normal bg-muted/50 p-4 whitespace-pre-wrap break-all",
            className,
          )}
        >
          {formatted}
        </pre>
      </div>
    );
  }

  return (
    <div className="group/code relative">
      {copyButton}
      <div
        className={cn(
          "min-w-0 overflow-auto rounded-md text-xs [&_pre]:min-w-max [&_pre]:p-4 [&_pre]:!bg-muted/50",
          className,
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
